import {toast} from "react-toastify";

/**
 * Determines if there was an error importing the file with CSV Box. This checks the CSV box result itself,
 * and for any external validation errors.
 *
 * @param result The CSV Box result.
 * @param data The data returned by CSV Box that contains the external validation error flag.
 */
export function csvBoxHasError(result: boolean, data: unknown): boolean {
    if(!result) {
        return true;
    }

    if(typeof data === "object" && data !== null) {
        const tmp = data as Record<string, unknown>;
        // External validation flag.
        const evFailed = tmp["ev_failed"];
        if(typeof evFailed === "number" && evFailed === 1) {
            return true;
        }
    }
    return false;
}

/**
 * Extracts the rows from a CSV box data object. Since the row type is dynamic,
 * an `unknown[]` is returned.
 *
 * @param data The CSV box data object.
 */
export function csvBoxExtractRows(data: unknown): unknown[] | undefined {
    if(typeof data !== "object") {
        return undefined;
    }

    const record = data as Record<string, unknown>;
    const rows = record["rows"];

    if(Array.isArray(rows)) {
        return rows;
    }
    return undefined;
}

export function csvBoxGeneralError(custom: string): void {
    toast.error(`Unknown error occurred importing ${custom}. Please try again later.`);
}

export function csvBoxExtractFilename(data: unknown): string | undefined {
    if(typeof data !== "object") {
        return undefined;
    }

    const record = data as Record<string, unknown>;
    const filename = record["original_filename"];
    if(typeof filename === "string") {
        return filename;
    }
    return undefined;
}

const monthNames: Record<string, number> = {
    "jan": 1, "feb": 2, "mar": 3, "apr": 4, "may": 5, "jun": 6,
    "jul": 7, "aug": 8, "sep": 9, "oct": 10, "nov": 11, "dec": 12
};

export function normalizeDateHeaders(header: string): string | null {
    const value = String(header || "").trim().replace(/\s+/g, " ");

    if (!value) {
        return null;
    }

    const normalize = (year: string, month: number): string => {
        if(year.length === 2) {
            return `20${year}-${month.toString().padStart(2, "0")}`;
        }
        return `${year}-${month.toString().padStart(2, "0")}`;
    };

    // Formats:
    // MM/YY, M/YY, MM/YYYY, M/YYYY
    let match = value.match(/^(\d{1,2})\/(\d{2}|\d{4})$/);
    if (match) {
        const year = match[2];
        const month = Number(match[1]);
        if(month >= 1 && month <= 12 && year) {
            return normalize(year, month);
        }
    }

    // Formats:
    // YYYY-MM, YY-MM, YY-M, YYYY-M
    match = value.match(/^(\d{2}|\d{4})-(\d{1,2})$/);
    if (match) {
        const year = match[1];
        const month = Number(match[2]);
        if(month >= 1 && month <= 12 && year) {
            return normalize(year, month);
        }
    }

    // Formats:
    // mmm YY, Aug 'YY, Aug YY, Aug  YYYY
    match = value.match(/^([A-Za-z]{3})\s+'?(\d{2}|\d{4})$/);
    if (match) {
        const year = match[2];
        const monthName = match[1]?.toLowerCase();
        if(monthName && monthNames[monthName] && year) {
            return normalize(year, monthNames[monthName]!);
        }
    }

    return null;
}

export function normalizeDate(input: string | null | undefined): string | null {
    // Allow empty optional date cells.
    if(input === null || input === undefined) {
        return "";
    }

    const value = input.trim().replace(/\s+/g, " ");

    // Allow empty optional date cells.
    if (!value) {
        return "";
    }

    let year;
    let month;
    let day;
    let match;

    // Formats:
    // YYYY-MM-DD, YY-MM-DD, YY-M-D, YYYY-M-D
    match = value.match(/^(\d{2}|\d{4})-(\d{1,2})-(\d{1,2})$/);

    if (match) {
        year = Number(match[1]);
        month = Number(match[2]);
        day = Number(match[3]);
    }

    // Formats:
    // MM/DD/YY, M/D/YY, MM/DD/YYYY, M/D/YYYY
    if (!match) {
        match = value.match(
            /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/
        );

        if (match) {
            month = Number(match[1]);
            day = Number(match[2]);
            year = Number(match[3]);
        }
    }

    // Formats:
    // Aug 1 26, Aug 1 '26, Aug 1 2026
    if (!match) {
        match = value.match(
            /^([A-Za-z]{3})\s+(\d{1,2})\s+'?(\d{2}|\d{4})$/
        );

        if (match) {
            month = monthNames[match[1]!.toLowerCase()];
            if(month === undefined) {
                return null;
            }
            day = Number(match[2]);
            year = Number(match[3]);
        }
    }

    // Unrecognized format.
    if (!match) {
        return null;
    }

    // Interpret two-digit years as 20YY.
    if(year === undefined) {
        return null;
    }

    if (year < 100) {
        year += 2000;
    }

    if(month === undefined) {
        return null;
    }

    // Validate the actual calendar date.
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
    ) {
        return null;
    }

    return [
        String(year).padStart(4, "0"),
        String(month).padStart(2, "0"),
        String(day).padStart(2, "0")
    ].join("-");
}